import type {
  GalleryPaginatedResponse,
  PhotoGalleryDTO,
  VideoGalleryDTO,
} from '~/shared/api/client/galleries/galleries.dto';
import type { EventMediaMode } from '~/shared/lib/events-routes.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { useOrigin } from '~/shared/lib/useOrigin';
import { EVENTS_GALLERY_DETAIL_CONFIG } from './gallery-detail.const';
import { useEventGalleryDetailSeo } from './seo';

type GalleryBase = PhotoGalleryDTO | VideoGalleryDTO;
type RelatedGalleryResponse<TGallery extends GalleryBase> =
  | GalleryPaginatedResponse<TGallery>
  | TGallery[];

function getRouteParamId(param: string | string[] | undefined): number {
  return Number(Array.isArray(param) ? param[0] : param);
}

export function useEventGalleryDetail<
  TDetail extends GalleryBase,
  TRelated extends GalleryBase,
>(mode: EventMediaMode) {
  const config = EVENTS_GALLERY_DETAIL_CONFIG[mode];
  const route = useRoute();
  const origin = useOrigin();

  const galleryId = computed(() => getRouteParamId(route.params.id));
  const requestUrl = computed(() => `${config.apiUrl}${galleryId.value}/`);

  const { data: galleryResponse, status } = useCustomFetch<TDetail>(
    () => requestUrl.value,
    {
      method: 'GET',
      key: `${config.detailKey}_${galleryId.value}`,
    },
  );

  const gallery = computed<TDetail | null>(
    () => (galleryResponse.value ?? null) as TDetail | null,
  );

  const { data: relatedResponse } = useCustomFetch<
    RelatedGalleryResponse<TRelated>
  >(config.apiUrl, {
    method: 'GET',
    key: `${config.relatedKey}_related`,
    query: {
      page_size: 4,
    },
  });

  const relatedGalleries = computed(() => {
    const response =
      relatedResponse.value as RelatedGalleryResponse<TRelated> | null;
    const list = Array.isArray(response) ? response : (response?.results ?? []);

    return list.filter((item) => item.id !== galleryId.value).slice(0, 3);
  });

  const title = computed(
    () => gallery.value?.description || gallery.value?.name || '',
  );
  const pageUrl = computed(() => `${origin}${route.fullPath}`);
  const isPending = computed(() => status.value === 'pending');
  const hasGallery = computed(() => Boolean(gallery.value));

  useEventGalleryDetailSeo(gallery, mode);

  return {
    config,
    gallery,
    galleryId,
    hasGallery,
    isPending,
    pageUrl,
    relatedGalleries,
    title,
  };
}
